// Remove trailing /api if present in the URL
const cleanApiUrl = (url) => url.replace(/\/api$/, '');
export const API_BASE_URL = `${cleanApiUrl(process.env.REACT_APP_API_URL || 'http://localhost:3000')}/api`;

import { handleAPIError } from './errorHandling';

// Helper function to handle API responses
export const handleResponse = async (response) => {
  try {
    if (!response.ok) {
      const errorData = await response.json();
      const error = new Error(errorData.details || errorData.error || 'API request failed');
      error.response = { data: errorData, status: response.status };
      // Try to handle the error with our enhanced error display
      await handleAPIError(error);
      // If handleAPIError doesn't reject (unlikely), throw the error anyway
      throw error;
    }
    return response.json();
  } catch (err) {
    if (err.response) {
      // Try to handle the error with our enhanced error display
      await handleAPIError(err);
      throw err;
    }
    throw new Error('Failed to parse response');
  }
};

// Import the enhanced CSRF debugging functions
import { getCSRFTokenWithDebug, checkCookieAccess, getAllCSRFCookies } from './csrfDebug';

// Helper function to get headers
export const getAuthHeader = async (method = 'GET') => {
  const headers = new Headers({
    'Content-Type': 'application/json'
  });

  // Add CSRF token for non-safe methods
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method.toUpperCase())) {
    try {
      // First check if cookies are accessible
      const cookieCheck = checkCookieAccess();
      if (!cookieCheck.cookiesEnabled) {
        console.error('Cookies are not accessible:', cookieCheck);
        throw new Error('Cookies are disabled or not accessible');
      }

      // Get CSRF token with enhanced debugging
      const { token: csrfToken, debug } = getCSRFTokenWithDebug();
      
      // Log debug info if token extraction fails or if there are issues
      if (!csrfToken || !debug.tokensAgree) {
        console.warn('CSRF token extraction issue:', {
          ...debug,
          allCSRFCookies: getAllCSRFCookies(),
          cookieCheck,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        });
      }
      
      if (csrfToken) {
        headers.set('x-csrf-token', csrfToken);
        
        // Log successful token extraction in development
        if (process.env.NODE_ENV === 'development') {
          console.debug('CSRF token set:', {
            tokenLength: csrfToken.length,
            tokenStart: csrfToken.substring(0, 4) + '...',
            method: debug.tokens[0]?.method
          });
        }
      } else {
        // Provide detailed error information
        const error = new Error('CSRF token validation failed');
        error.response = {
          data: {
            error: 'Invalid CSRF token',
            details: `CSRF token could not be extracted from cookies`,
            debug: {
              ...debug,
              cookieAccess: cookieCheck,
              allCSRFCookies: getAllCSRFCookies(),
              userAgent: navigator.userAgent
            }
          }
        };
        throw error;
      }
    } catch (error) {
      console.error('Error extracting CSRF token:', error);
      // Re-throw to handle upstream
      throw error;
    }
  }

  return headers;
};

export const preferences = {
  update: async (data) => {
    try {
      const headers = await getAuthHeader('PUT');
      const putResponse = await fetch(`${API_BASE_URL}/users/profile/preferences`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data),
        credentials: 'include'
      });
      return handleResponse(putResponse);
    } catch (err) {
      console.error('Failed to update preferences:', err);
      throw err;
    }
  },

  get: async () => {
    try {
      const headers = await getAuthHeader('GET');
      const response = await fetch(`${API_BASE_URL}/users/profile/preferences`, {
        method: 'GET',
        headers,
        credentials: 'include'
      });
      return handleResponse(response);
    } catch (err) {
      console.error('Failed to get preferences:', err);
      throw err;
    }
  }
};

export const auth = {
  getCurrentUser: async () => {
    try {
      const headers = await getAuthHeader('GET');
      const response = await fetch(`${API_BASE_URL}/users/auth/me`, {
        headers,
        credentials: 'include'
      });
      
      const result = await handleResponse(response, 'GET');
      
      // Set avatar URL if available
      if (result.avatar_url) {
        result.avatarBlobUrl = images.getImage(result.avatar_url);
      }
      
      return result;
    } catch (err) {
      console.error('Failed to get current user:', err);
      throw err;
    }
  },

  login: async (email, password) => {
    try {
      const headers = new Headers({
        'Content-Type': 'application/json'
      });
      
      const response = await fetch(`${API_BASE_URL}/users/auth/login`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ email, password }),
        credentials: 'include'
      });

      await handleResponse(response);
      return auth.getCurrentUser();
    } catch (err) {
      console.error('Login failed:', err);
      throw err;
    }
  },

  register: async (email, password, first_name, last_name, invite_code, role_id) => {
    try {
      const headers = new Headers({
        'Content-Type': 'application/json'
      });
      
      const response = await fetch(`${API_BASE_URL}/users/auth/register`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ email, password, first_name, last_name, invite_code, role_id }),
        credentials: 'include'
      });

      return handleResponse(response);
    } catch (err) {
      console.error('Registration failed:', err);
      throw err;
    }
  },

  logout: async () => {
    try {
      const headers = await getAuthHeader('POST');
      const response = await fetch(`${API_BASE_URL}/users/auth/logout`, {
        method: 'POST',
        headers,
        credentials: 'include'
      });
      return handleResponse(response, 'POST');
    } catch (err) {
      console.error('Logout failed:', err);
      throw err;
    }
  },

  updateProfile: async (data) => {
    const headers = await getAuthHeader('PUT');
    const response = await fetch(`${API_BASE_URL}/users/profile`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
      credentials: 'include'
    });
    return handleResponse(response, 'PUT');
  },

  uploadAvatar: async (file) => {
    const formData = new FormData();
    formData.append('avatar', file);

    // Get auth headers but exclude Content-Type
    const headers = await getAuthHeader('POST');
    const finalHeaders = new Headers();
    
    // Copy over CSRF token if present
    const csrfToken = headers.get('x-csrf-token');
    if (csrfToken) {
      finalHeaders.set('x-csrf-token', csrfToken);
    }

    const response = await fetch(`${API_BASE_URL}/users/avatar`, {
      method: 'POST',
      headers: finalHeaders,
      body: formData,
      credentials: 'include'
    });
    return handleResponse(response, 'POST');
  },

  deleteAvatar: async () => {
    const headers = await getAuthHeader('DELETE');
    const response = await fetch(`${API_BASE_URL}/users/avatar`, {
      method: 'DELETE',
      headers,
      credentials: 'include'
    });
    return handleResponse(response, 'DELETE');
  },

  getAvatarUrl: (filename) => {
    if (!filename) return null;
    return `${API_BASE_URL}/users/avatar/${filename}`;
  },

  getAvatar: async (filename) => {
    if (!filename) return null;
    const headers = await getAuthHeader('GET');
    const response = await fetch(`${API_BASE_URL}/users/avatar/${filename}`, {
      headers,
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to fetch avatar');
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  },

  changePassword: async (currentPassword, newPassword) => {
    const headers = await getAuthHeader('PUT');
    const response = await fetch(`${API_BASE_URL}/users/password/change`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ currentPassword, newPassword }),
      credentials: 'include'
    });
    return handleResponse(response, 'PUT');
  },

  getAllUsers: async () => {
    const headers = await getAuthHeader('GET');
    const response = await fetch(`${API_BASE_URL}/admin/users`, {
      headers,
      credentials: 'include'
    });
      return handleResponse(response);
  },

  deleteUser: async (userId) => {
    const headers = await getAuthHeader('DELETE');
    const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
      method: 'DELETE',
      headers,
      credentials: 'include'
    });
    return handleResponse(response, 'DELETE');
  },

  deleteUserData: async (userId) => {
    const headers = await getAuthHeader('DELETE');
    const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/data`, {
      method: 'DELETE',
      headers,
      credentials: 'include'
    });
    return handleResponse(response, 'DELETE');
  },

  resetRateLimits: async () => {
    const headers = await getAuthHeader('POST');
    const response = await fetch(`${API_BASE_URL}/admin/reset-rate-limits`, {
      method: 'POST',
      headers,
      credentials: 'include'
    });
    return handleResponse(response, 'POST');
  },

  checkAllPCGSStatus: async () => {
    const headers = await getAuthHeader('POST');
    const response = await fetch(`${API_BASE_URL}/admin/check-all-pcgs-status`, {
      method: 'POST',
      headers,
      credentials: 'include'
    });
    return handleResponse(response, 'POST');
  },

  // PCGS Cache Management
  getPcgsCache: async (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    
    const headers = await getAuthHeader('GET');
    const response = await fetch(`${API_BASE_URL}/pcgs-cache${queryString}`, {
      headers,
      credentials: 'include'
    });
    return handleResponse(response);
  },

  getPcgsCacheRecord: async (pcgsNumber) => {
    const headers = await getAuthHeader('GET');
    const response = await fetch(`${API_BASE_URL}/pcgs-cache/${pcgsNumber}`, {
      headers,
      credentials: 'include'
    });
    return handleResponse(response, 'GET');
  },

  createPcgsCacheRecord: async (data) => {
    const headers = await getAuthHeader('POST');
    const response = await fetch(`${API_BASE_URL}/pcgs-cache`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
      credentials: 'include'
    });
    return handleResponse(response, 'POST');
  },

  updatePcgsCacheRecord: async (pcgsNumber, data) => {
    const headers = await getAuthHeader('PUT');
    const response = await fetch(`${API_BASE_URL}/pcgs-cache/${pcgsNumber}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
      credentials: 'include'
    });
    return handleResponse(response, 'PUT');
  },

  deletePcgsCacheRecord: async (pcgsNumber) => {
    const headers = await getAuthHeader('DELETE');
    const response = await fetch(`${API_BASE_URL}/pcgs-cache/${pcgsNumber}`, {
      method: 'DELETE',
      headers,
      credentials: 'include'
    });
    return handleResponse(response, 'DELETE');
  },

  // System Auction Lots Management
  getSystemAuctionLots: async (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    
    const headers = await getAuthHeader('GET');
    const response = await fetch(`${API_BASE_URL}/system-auction-lots${queryString}`, {
      headers,
      credentials: 'include'
    });
    return handleResponse(response);
  },

  getSystemAuctionLot: async (id) => {
    const headers = await getAuthHeader('GET');
    const response = await fetch(`${API_BASE_URL}/system-auction-lots/${id}`, {
      headers,
      credentials: 'include'
    });
    return handleResponse(response, 'GET');
  },

  createSystemAuctionLot: async (data) => {
    const headers = await getAuthHeader('POST');
    const response = await fetch(`${API_BASE_URL}/system-auction-lots`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
      credentials: 'include'
    });
    return handleResponse(response, 'POST');
  },

  updateSystemAuctionLot: async (id, data) => {
    const headers = await getAuthHeader('PUT');
    const response = await fetch(`${API_BASE_URL}/system-auction-lots/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
      credentials: 'include'
    });
    return handleResponse(response, 'PUT');
  },

  deleteSystemAuctionLot: async (id) => {
    const headers = await getAuthHeader('DELETE');
    const response = await fetch(`${API_BASE_URL}/system-auction-lots/${id}`, {
      method: 'DELETE',
      headers,
      credentials: 'include'
    });
    return handleResponse(response, 'DELETE');
  },

  forgotPassword: async (email) => {
    const headers = new Headers({
      'Content-Type': 'application/json'
    });
    
    const response = await fetch(`${API_BASE_URL}/users/password/forgot`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ email }),
      credentials: 'include'
    });
    return handleResponse(response, 'POST');
  },

  resetPassword: async (token, newPassword) => {
    const headers = new Headers({
      'Content-Type': 'application/json'
    });
    
    const response = await fetch(`${API_BASE_URL}/users/password/reset`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ token, newPassword }),
      credentials: 'include'
    });
    return handleResponse(response, 'POST');
  },
};

export const images = {
  getImage: (filepath, source = null) => {
    if (!filepath) return null;
    
    // Extract filename from filepath
    const filename = filepath.split('/').pop();
    
    // Determine bucket and domain based on filepath pattern and source
    let bucket, domain;
    const isThumbnail = filepath.includes('/thumbnails/') || filepath.includes('/auctions/thumbnails/');
    
    if (filepath.includes('/avatar')) {
      bucket = process.env.REACT_APP_R2_BUCKET_AVATARS;
      domain = process.env.REACT_APP_R2_DOMAIN_AVATARS;
    } else if (filepath.includes('/logo')) {
      bucket = process.env.REACT_APP_R2_BUCKET_LOGOS;
      domain = process.env.REACT_APP_R2_DOMAIN_LOGOS;
    } else if (source === 'cert_lookup' || (!source && filepath.includes('/cert/'))) {
      bucket = isThumbnail ? process.env.REACT_APP_R2_BUCKET_CERT_THUMBS : process.env.REACT_APP_R2_BUCKET_CERT_IMAGES;
      domain = isThumbnail ? process.env.REACT_APP_R2_DOMAIN_CERT_THUMBS : process.env.REACT_APP_R2_DOMAIN_CERT;
    } else if (source === 'auction_house' || (!source && filepath.includes('/auctions/'))) {
      bucket = isThumbnail ? process.env.REACT_APP_R2_BUCKET_AUCTION_THUMBS : process.env.REACT_APP_R2_BUCKET_AUCTION_IMAGES;
      domain = isThumbnail ? process.env.REACT_APP_R2_DOMAIN_AUCTION_THUMBS : process.env.REACT_APP_R2_DOMAIN_AUCTION;
    } else {
      // Default to user_upload if no source specified
      bucket = isThumbnail ? process.env.REACT_APP_R2_BUCKET_USER_THUMBS : process.env.REACT_APP_R2_BUCKET_USER_IMAGES;
      domain = isThumbnail ? process.env.REACT_APP_R2_DOMAIN_USER_THUMBS : process.env.REACT_APP_R2_DOMAIN_USER;
    }

    return `https://${domain}/${filename}`;
  },

  upload: async (coinId, files) => {
    const formData = new FormData();
    Array.from(files).forEach(file => {
      formData.append('images', file);
    });

    const headers = await getAuthHeader('POST');
    const finalHeaders = new Headers();
    const csrfToken = headers.get('x-csrf-token');
    if (csrfToken) {
      finalHeaders.set('x-csrf-token', csrfToken);
    }

    const response = await fetch(`${API_BASE_URL}/images/upload/${coinId}`, {
      method: 'POST',
      headers: finalHeaders,
      body: formData,
      credentials: 'include'
    });
    return handleResponse(response, 'POST');
  },

  reorder: async (coinId, imageIds) => {
    const headers = await getAuthHeader('PUT');
    const response = await fetch(`${API_BASE_URL}/images/reorder/${coinId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ imageIds }),
      credentials: 'include'
    });
      return handleResponse(response);
  },

  delete: async (imageId) => {
    const headers = await getAuthHeader('DELETE');
    const response = await fetch(`${API_BASE_URL}/images/delete/${imageId}`, {
      method: 'DELETE',
      headers,
      credentials: 'include'
    });
      return handleResponse(response);
  },

  checkAuctionImages: async (service, certNumber, coinId) => {
    const headers = await getAuthHeader('GET');
    const response = await fetch(`${API_BASE_URL}/images/check-auction/${service}/${certNumber}/${coinId}`, {
      headers,
      credentials: 'include'
    });
    return handleResponse(response);
  },

  copyAuctionImages: async (coinId, images) => {
    const headers = await getAuthHeader('POST');
    const response = await fetch(`${API_BASE_URL}/images/copy-auction/${coinId}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ images }),
      credentials: 'include'
    });
    return handleResponse(response);
  }
};

export const coins = {
  getColumns: async () => {
    const headers = await getAuthHeader('GET');
    const response = await fetch(`${API_BASE_URL}/coins/columns`, {
      headers,
      credentials: 'include'
    });
      return handleResponse(response);
  },

  getFilterOptions: async ({ userId } = {}) => {
    try {
      const headers = await getAuthHeader('GET');
      const response = await fetch(`${API_BASE_URL}/coins/filter-options${userId ? `?userId=${userId}` : ''}`, {
        headers,
        credentials: 'include'
      });
      return handleResponse(response);
    } catch (err) {
      console.error('Failed to fetch filter options:', err.message);
      throw err;
    }
  },

  getAll: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach(v => {
          if (v !== undefined && v !== null && v !== '') {
            params.append(key, v);
          }
        });
      } else if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });

    const url = `${API_BASE_URL}/coins${params.toString() ? `?${params.toString()}` : ''}`;
    const headers = await getAuthHeader('GET');
    const response = await fetch(url, {
      headers,
      credentials: 'include'
    });
    return handleResponse(response);
  },

  getById: async (id) => {
    const headers = await getAuthHeader('GET');
    const response = await fetch(`${API_BASE_URL}/coins/${id}`, {
      headers,
      credentials: 'include'
    });
    return handleResponse(response);
  },

  getAdjacentIds: async (id, filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach(v => {
          if (v !== undefined && v !== null && v !== '') {
            params.append(key, v);
          }
        });
      } else if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });

    const headers = await getAuthHeader('GET');
    const response = await fetch(
      `${API_BASE_URL}/coins/${id}/adjacent${params.toString() ? `?${params.toString()}` : ''}`,
      {
        headers,
        credentials: 'include'
      }
    );
    return handleResponse(response);
  },

  create: async (data) => {
    const headers = await getAuthHeader('POST');
    const response = await fetch(`${API_BASE_URL}/coins`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
      credentials: 'include'
    });
      return handleResponse(response);
  },

  update: async (id, data) => {
    const headers = await getAuthHeader('PUT');
    const response = await fetch(`${API_BASE_URL}/coins/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
      credentials: 'include'
    });
      return handleResponse(response);
  },

  delete: async (id) => {
    const headers = await getAuthHeader('DELETE');
    const response = await fetch(`${API_BASE_URL}/coins/${id}`, {
      method: 'DELETE',
      headers,
      credentials: 'include'
    });
      return handleResponse(response);
  },

  bulkDelete: async (coinIds) => {
    const headers = await getAuthHeader('DELETE');
    const response = await fetch(`${API_BASE_URL}/coins/bulk`, {
      method: 'DELETE',
      headers,
      body: JSON.stringify({ coinIds }),
      credentials: 'include'
    });
    return handleResponse(response, 'DELETE');
  },

  getPossibleStatuses: async () => {
    const headers = await getAuthHeader('GET');
    const response = await fetch(`${API_BASE_URL}/coins/possible-statuses`, {
      headers,
      credentials: 'include'
    });
    return handleResponse(response, 'GET');
  },

  bulkUpdateStatus: async (coinIds, status_name) => {
    const headers = await getAuthHeader('PUT');
    const response = await fetch(`${API_BASE_URL}/coins/bulk/status`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ coinIds, status: status_name }),
      credentials: 'include'
    });
    return handleResponse(response, 'PUT');
  },

  createCustomStatus: async (data) => {
    const headers = await getAuthHeader('POST');
    const response = await fetch(`${API_BASE_URL}/coins/custom-status`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
      credentials: 'include'
    });
    return handleResponse(response);
  },

  updateCustomStatus: async (id, data) => {
    const headers = await getAuthHeader('PUT');
    const response = await fetch(`${API_BASE_URL}/coins/custom-status/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
      credentials: 'include'
    });
    return handleResponse(response);
  },

  deleteCustomStatus: async (id) => {
    const headers = await getAuthHeader('DELETE');
    const response = await fetch(`${API_BASE_URL}/coins/custom-status/${id}`, {
      method: 'DELETE',
      headers,
      credentials: 'include'
    });
    return handleResponse(response);
  },

  getNextUserCoinId: async () => {
    const headers = await getAuthHeader('GET');
    const response = await fetch(`${API_BASE_URL}/coins/next-user-coin-id`, {
      headers,
      credentials: 'include'
    });
    return handleResponse(response, 'GET');
  },

  getSubmissionHistory: async (id) => {
    const headers = await getAuthHeader('GET');
    const response = await fetch(`${API_BASE_URL}/coins/${id}/submissions`, {
      headers,
      credentials: 'include'
    });
    return handleResponse(response, 'GET');
  },

  getStorageLocations: async (search) => {
    const headers = await getAuthHeader('GET');
    const url = `${API_BASE_URL}/coins/storage-locations${search ? `?search=${encodeURIComponent(search)}` : ''}`;
    const response = await fetch(url, {
      headers,
      credentials: 'include'
    });
    return handleResponse(response, 'GET');
  },

  getPopulationHistory: async (coinId) => {
    const headers = await getAuthHeader('GET');
    const response = await fetch(`${API_BASE_URL}/coins/${coinId}/population-history`, {
      headers,
      credentials: 'include'
    });
    return handleResponse(response, 'GET');
  },
};

export const contacts = {
  create: async (data, params = {}) => {
    const requestData = {
      ...data,
      created_by: params.userId
    };

    const headers = await getAuthHeader('POST');
    const response = await fetch(`${API_BASE_URL}/contacts`, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestData),
      credentials: 'include'
    });
    return handleResponse(response);
  },

  getAll: async (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const headers = await getAuthHeader('GET');
    const response = await fetch(`${API_BASE_URL}/contacts${queryString}`, {
      headers,
      credentials: 'include'
    });
    return handleResponse(response, 'GET');
  },

  getById: async (id, params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const headers = await getAuthHeader('GET');
    const response = await fetch(`${API_BASE_URL}/contacts/${id}${queryString}`, {
      headers,
      credentials: 'include'
    });
    return handleResponse(response, 'GET');
  },

  update: async (id, data, params = {}) => {
    try {
      const requestData = {
        ...data,
        created_by: params.userId
      };

      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';

      const headers = await getAuthHeader('PUT');
      const response = await fetch(`${API_BASE_URL}/contacts/${id}${queryString}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(requestData),
        credentials: 'include'
      });
      
      return handleResponse(response);
    } catch (err) {
      console.error('Failed to update contact:', err);
      throw err;
    }
  },

  delete: async (id, params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    
    const headers = await getAuthHeader('DELETE');
    const response = await fetch(`${API_BASE_URL}/contacts/${id}${queryString}`, {
      method: 'DELETE',
      headers,
      credentials: 'include'
    });
      return handleResponse(response);
  },
};

export const transactions = {
  create: async (data) => {
    const headers = await getAuthHeader('POST');
    const response = await fetch(`${API_BASE_URL}/transactions`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
      credentials: 'include'
    });
      return handleResponse(response);
  },

  getAll: async (params) => {
    const queryString = params instanceof URLSearchParams ? `?${params.toString()}` : '';
    const headers = await getAuthHeader('GET');
    const response = await fetch(`${API_BASE_URL}/transactions${queryString}`, {
      headers,
      credentials: 'include'
    });
    return handleResponse(response);
  },

  getById: async (id) => {
    const headers = await getAuthHeader('GET');
    const response = await fetch(`${API_BASE_URL}/transactions/${id}`, {
      headers,
      credentials: 'include'
    });
    return handleResponse(response, 'GET');
  },

  update: async (id, data) => {
    const headers = await getAuthHeader('PUT');
    const response = await fetch(`${API_BASE_URL}/transactions/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
      credentials: 'include'
    });
    return handleResponse(response);
  },

  delete: async (id) => {
    const headers = await getAuthHeader('DELETE');
    const response = await fetch(`${API_BASE_URL}/transactions/${id}`, {
      method: 'DELETE',
      headers,
      credentials: 'include'
    });
    return handleResponse(response);
  },
};

export const certificationServices = {
  getAll: async () => {
    const headers = await getAuthHeader('GET');
    const response = await fetch(`${API_BASE_URL}/certification-services`, {
      headers,
      credentials: 'include'
    });
    return handleResponse(response, 'GET');
  },
};

export const certLookup = {
  lookup: async (service, certNumber, grade = undefined, isPCGSNumber = false) => {
    const url = new URL(`${API_BASE_URL}/cert-lookup/${service}/${certNumber}`);
    if (grade !== undefined && grade !== null && grade !== '') {
      url.searchParams.append('grade', grade);
    }
    if (isPCGSNumber) {
      url.searchParams.append('isPCGSNumber', 'true');
    }
    const headers = await getAuthHeader('GET');
    const response = await fetch(url, {
      headers,
      credentials: 'include'
    });
    return handleResponse(response, 'GET');
  },
};

export const tags = {
  getAll: async () => {
    const headers = await getAuthHeader('GET');
    const response = await fetch(`${API_BASE_URL}/tags`, {
      headers,
      credentials: 'include'
    });
    return handleResponse(response, 'GET');
  },

  create: async (name, color) => {
    const headers = await getAuthHeader('POST');
    const response = await fetch(`${API_BASE_URL}/tags`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ name, color }),
      credentials: 'include'
    });
    return handleResponse(response, 'POST');
  },

  update: async (id, name, color) => {
    const headers = await getAuthHeader('PUT');
    const response = await fetch(`${API_BASE_URL}/tags/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ name, color }),
      credentials: 'include'
    });
    return handleResponse(response, 'PUT');
  },

  delete: async (id) => {
    const headers = await getAuthHeader('DELETE');
    const response = await fetch(`${API_BASE_URL}/tags/${id}`, {
      method: 'DELETE',
      headers,
      credentials: 'include'
    });
    return handleResponse(response, 'DELETE');
  },

  addTagsToCoin: async (coinId, tagIds) => {
    const headers = await getAuthHeader('POST');
    const response = await fetch(`${API_BASE_URL}/tags/coin/${coinId}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ tagIds }),
      credentials: 'include'
    });
    return handleResponse(response, 'POST');
  },

  addTagsToCoins: async (coinIds, tagIds) => {
    const headers = await getAuthHeader('POST');
    const response = await fetch(`${API_BASE_URL}/tags/coins/bulk`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ coinIds, tagIds }),
      credentials: 'include'
    });
    return handleResponse(response, 'POST');
  },

  removeTagFromCoin: async (coinId, tagId) => {
    const headers = await getAuthHeader('DELETE');
    const response = await fetch(`${API_BASE_URL}/tags/coin/${coinId}/${tagId}`, {
      method: 'DELETE',
      headers,
      credentials: 'include'
    });
    return handleResponse(response, 'DELETE');
  },

  removeTagsFromCoins: async (coinIds, tagIds) => {
    const headers = await getAuthHeader('POST');
    const response = await fetch(`${API_BASE_URL}/tags/coins/bulk-remove`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ coinIds, tagIds }),
      credentials: 'include'
    });
    return handleResponse(response, 'POST');
  },

  getTagCoins: async (tagId) => {
    const headers = await getAuthHeader('GET');
    const response = await fetch(`${API_BASE_URL}/tags/${tagId}/coins`, {
      headers,
      credentials: 'include'
    });
    return handleResponse(response, 'GET');
  }
};

export const labelTemplates = {
  getAll: async () => {
    const headers = await getAuthHeader('GET');
    const response = await fetch(`${API_BASE_URL}/label-templates`, {
      headers,
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch templates');
    }

    return response.json();
  },

  getById: async (id) => {
    const headers = await getAuthHeader('GET');
    const response = await fetch(`${API_BASE_URL}/label-templates/${id}`, {
      headers,
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch template');
    }

    return response.json();
  },

  create: async (data) => {
    const headers = await getAuthHeader('POST');
    const response = await fetch(`${API_BASE_URL}/label-templates`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create template');
    }

    return response.json();
  },

  update: async (id, data) => {
    const headers = await getAuthHeader('PUT');
    const response = await fetch(`${API_BASE_URL}/label-templates/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update template');
    }

    return response.json();
  },

  delete: async (id) => {
    const headers = await getAuthHeader('DELETE');
    const response = await fetch(`${API_BASE_URL}/label-templates/${id}`, {
      method: 'DELETE',
      headers,
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete template');
    }

    return response.json();
  }
};

export const documents = {
  getAll: async (filter) => {
    const params = new URLSearchParams();
    if (filter) {
      params.append('filter', filter);
    }
    const headers = await getAuthHeader('GET');
    const response = await fetch(`${API_BASE_URL}/documents${filter ? `?${params}` : ''}`, {
      headers,
      credentials: 'include'
    });
    return handleResponse(response, 'GET');
  },

  getTypes: async () => {
    const headers = await getAuthHeader('GET');
    const response = await fetch(`${API_BASE_URL}/documents/types`, {
      headers,
      credentials: 'include'
    });
    return handleResponse(response, 'GET');
  },

  upload: async (formData) => {
    const headers = await getAuthHeader('POST');
    const finalHeaders = new Headers();
    const csrfToken = headers.get('x-csrf-token');
    if (csrfToken) {
      finalHeaders.set('x-csrf-token', csrfToken);
    }

    const response = await fetch(`${API_BASE_URL}/documents`, {
      method: 'POST',
      headers: finalHeaders,
      body: formData,
      credentials: 'include'
    });
    return handleResponse(response, 'POST');
  },

  delete: async (id) => {
    const headers = await getAuthHeader('DELETE');
    const response = await fetch(`${API_BASE_URL}/documents/${id}`, {
      method: 'DELETE',
      headers,
      credentials: 'include'
    });
    return handleResponse(response, 'DELETE');
  },

  download: async (id) => {
    const headers = await getAuthHeader('GET');
    const response = await fetch(`${API_BASE_URL}/documents/${id}/download`, {
      headers,
      credentials: 'include'
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to download document');
    }
    return response.blob();
  },

  bulkDelete: async (documentIds) => {
    const headers = await getAuthHeader('POST');
    const response = await fetch(`${API_BASE_URL}/documents/bulk-delete`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ documentIds }),
      credentials: 'include'
    });
    return handleResponse(response, 'POST');
  },

  getById: async (id) => {
    const headers = await getAuthHeader('GET');
    const response = await fetch(`${API_BASE_URL}/documents/${id}`, {
      headers,
      credentials: 'include'
    });
    return handleResponse(response);
  },

  update: async (id, data) => {
    const headers = await getAuthHeader('PUT');
    const response = await fetch(`${API_BASE_URL}/documents/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
      credentials: 'include'
    });
    return handleResponse(response);
  }
};

export { sets } from './api/sets';
export { customFields } from './api/customFields';

export const submissions = {
  getAll: async () => {
    const headers = await getAuthHeader('GET');
    const response = await fetch(`${API_BASE_URL}/submissions`, {
      headers,
      credentials: 'include'
    });
    return handleResponse(response, 'GET');
  },

  getById: async (id) => {
    const headers = await getAuthHeader('GET');
    const response = await fetch(`${API_BASE_URL}/submissions/${id}`, {
      headers,
      credentials: 'include'
    });
    return handleResponse(response, 'GET');
  },

  create: async (data) => {
    const headers = await getAuthHeader('POST');
    const response = await fetch(`${API_BASE_URL}/submissions`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
      credentials: 'include'
    });
    return handleResponse(response, 'POST');
  },

  updateStatus: async (id, statusData) => {
    const headers = await getAuthHeader('PUT');
    const response = await fetch(`${API_BASE_URL}/submissions/${id}/status`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(statusData),
      credentials: 'include'
    });
    return handleResponse(response, 'PUT');
  },

  checkPCGSStatus: async (id) => {
    const headers = await getAuthHeader('PUT');
    const response = await fetch(`${API_BASE_URL}/submissions/${id}/status`, {
      method: 'PUT',
      headers,
      credentials: 'include'
    });
    return handleResponse(response, 'PUT');
  },

  checkAllPCGSStatus: async () => {
    const headers = await getAuthHeader('POST');
    const response = await fetch(`${API_BASE_URL}/submissions/check-pcgs-status`, {
      method: 'POST',
      headers,
      credentials: 'include'
    });
    return handleResponse(response, 'POST');
  },

  update: async (id, data) => {
    const headers = await getAuthHeader('PUT');
    const response = await fetch(`${API_BASE_URL}/submissions/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
      credentials: 'include'
    });
    return handleResponse(response, 'PUT');
  },

  delete: async (id) => {
    const headers = await getAuthHeader('DELETE');
    const response = await fetch(`${API_BASE_URL}/submissions/${id}`, {
      method: 'DELETE',
      headers,
      credentials: 'include'
    });
    return handleResponse(response, 'DELETE');
  }
};
